import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useReveal } from "@/hooks/use-reveal";
import { CATEGORIES, TERMS, termsByCategory, categoryLabel } from "@/content/glossary";


const TITLE = "Business terms glossary for founders";
const DESCRIPTION =
  "Plain-language definitions of the business terms founders actually meet: cap table, burn rate, runway, CAC, LTV, MRR, product-market fit, gross margin and more.";

export const Route = createFileRoute("/glossary/")({
  head: () => ({
    meta: [
      { title: "Business Glossary: Plain-Language Terms for Founders" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/glossary" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/glossary" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "The Founders App business glossary",
          description: DESCRIPTION,
          url: "https://thefoundersapp.com/glossary",
          hasDefinedTerm: TERMS.map((t) => ({
            "@type": "DefinedTerm",
            name: t.term,
            description: t.definition,
            url: `https://thefoundersapp.com/glossary/${t.slug}`,
          })),
        }),
      },
    ],
  }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  useReveal();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!q) return null;
    return TERMS.filter((t) =>
      `${t.term} ${t.definition} ${t.body.join(" ")}`.toLowerCase().includes(q),
    ).sort((a, b) => a.term.localeCompare(b.term));
  }, [q]);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-5xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">Glossary</p>
        <h1 className="reveal etched mt-5 max-w-3xl text-[2.5rem] leading-[0.95] font-semibold sm:text-6xl [--d:80ms]">
          {TITLE}
        </h1>
        <p className="reveal mt-7 max-w-xl text-[0.95rem] leading-relaxed text-muted-foreground [--d:160ms]">
          {TERMS.length} terms, defined in one sentence and then explained properly — what each one
          means, why it matters when you are the one running the business, and a realistic example.
          No jargon defended with more jargon.
        </p>

        <div className="reveal mt-8 max-w-md [--d:180ms]">
          <label htmlFor="glossary-search" className="micro-label block">
            Search terms
          </label>
          <div className="relative mt-3">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="glossary-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="burn rate, churn, cap table…"
              className="w-full rounded-sm border border-border bg-transparent py-2.5 pr-3 pl-9 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-foreground/40"
            />
          </div>
        </div>

        {matches === null ? (
          <>
            <nav aria-label="Categories" className="reveal mt-10 flex flex-wrap gap-2 [--d:200ms]">
              {CATEGORIES.map((c) => (
                <a
                  key={c.key}
                  href={`#${c.key}`}
                  className="micro-label rounded-sm border border-border px-3 py-2 transition-colors hover:border-foreground/40 hover:text-foreground"
                >
                  {c.label}
                </a>
              ))}
            </nav>

            <div className="hairline scan-rule mt-12" />

            <div className="mt-12 space-y-14">
              {CATEGORIES.map((category) => (
                <section key={category.key} id={category.key} className="scroll-mt-24">
                  <h2 className="reveal text-xl font-medium sm:text-2xl">{category.label}</h2>
                  <p className="reveal mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                    {category.blurb}
                  </p>

                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                    {termsByCategory(category.key).map((term, i) => (
                      <li key={term.slug}>
                        <Link
                          to="/glossary/$slug"
                          params={{ slug: term.slug }}
                          className="reveal soft-panel block h-full p-5 transition-colors hover:border-foreground/25"
                          style={{ ["--d" as string]: `${60 + i * 30}ms` }}
                        >
                          <span className="block text-base font-medium">{term.term}</span>
                          <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                            {term.definition}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </>
        ) : (
          <section aria-live="polite" className="mt-10">
            <p className="micro-label">
              {matches.length} {matches.length === 1 ? "match" : "matches"} for “{query.trim()}”
            </p>
            {matches.length === 0 ? (
              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
                Nothing here uses that word. Try a shorter search, or clear it to browse all{" "}
                {TERMS.length} terms.
              </p>
            ) : (
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {matches.map((term) => (
                  <li key={term.slug}>
                    <Link
                      to="/glossary/$slug"
                      params={{ slug: term.slug }}
                      className="soft-panel block h-full p-5 transition-colors hover:border-foreground/25"
                    >
                      <span className="block text-base font-medium">{term.term}</span>
                      <span className="mt-1 block text-xs text-muted-foreground/80">
                        {categoryLabel(term.category)}
                      </span>
                      <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                        {term.definition}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

